import { yupResolver } from "@hookform/resolvers/yup";
import "./IncomeHandler.scss";
import { useL10n } from "../../../../../l10n";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useElementId } from "../../../../../hooks";
import { DoraButton, UFDoraTextInput } from "dora-shared/frontend";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ValidationError from "./ValidationError";

const EditSurchargeRow = (props: {
  line: { id: string; percentage: string };
  onUpdateSurcharge: (id: string, percentage: string) => Promise<unknown>;
  onCancel: () => void;
  noCheckboxes: boolean;
}) => {
  const { line, onUpdateSurcharge, onCancel, noCheckboxes } = props;
  const l10n = useL10n();
  const schema = useMemo(
    () =>
      yup.object({
        percentage: yup
          .number()
          .required()
          .positive()
          .transform((_, strValue) => l10n.parseNumber(strValue)),
      }),
    [l10n]
  );
  const defaultValues = {
    percentage: l10n.formatNumber(line.percentage),
  };
  const fieldId = useElementId();
  const formMethods = useForm<{ percentage: string }>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { register, handleSubmit } = formMethods;
  const errors = formMethods.formState.errors;
  const inputValidationProps = useCallback(
    (id: string, field: "percentage") => {
      const fieldError = formMethods.formState.errors[field];
      return fieldError
        ? { "aria-invalid": true, "aria-describedby": id, error: true }
        : {};
    },
    [formMethods.formState]
  );
  // Guards against the save button firing more than once for the same edit
  // (fast double-click, Enter auto-repeat, a duplicate submit event) — without
  // this, two identical requests can race the delivery's optimistic-concurrency
  // check and the loser comes back as a spurious 409.
  const isSubmittingRef = useRef(false);
  const [isSubmittingRow, setIsSubmittingRow] = useState(false);

  const submitRow = useMemo(() => {
    return handleSubmit(
      async (values) => {
        if (isSubmittingRef.current) {
          return;
        }
        isSubmittingRef.current = true;
        setIsSubmittingRow(true);
        try {
          await onUpdateSurcharge(line.id, values.percentage.toString());
          onCancel();
        } catch (err) {
          console.error(err);
        } finally {
          isSubmittingRef.current = false;
          setIsSubmittingRow(false);
        }
      },
      (fieldErrors) => {}
    );
  }, [handleSubmit, onUpdateSurcharge, onCancel, line.id]);
  const { t } = useTranslation(["components"]);
  return (
    <FormProvider {...formMethods}>
      <form role="row form" className="income-grid__row" onSubmit={submitRow}>
        {!noCheckboxes && (
          <div className="add-invoice-line-row__cell" role="gridcell">
            {/* selected */}
          </div>
        )}
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell">
          <UFDoraTextInput
            register={register("percentage")}
            aria-label={t("components:percentage")}
            {...inputValidationProps(fieldId, "percentage")}
          />
        </div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__cell" role="gridcell"></div>
        <div className="add-invoice-line-row__last-cell" role="gridcell">
          <DoraButton
            variant="primary"
            type="button"
            className={"add-invoice-line-last-cell__button"}
            title={t("components:buttonLabels.cancel")}
            aria-label={t("components:buttonLabels.cancel")}
            onClick={onCancel}
            disabled={isSubmittingRow}
          >
            <FontAwesomeIcon icon={faTimes} />
          </DoraButton>
          <DoraButton
            variant="primary"
            className={"add-invoice-line-last-cell__button"}
            type="submit"
            title={t("components:buttonLabels.save")}
            aria-label={t("components:buttonLabels.save")}
            disabled={isSubmittingRow}
          >
            <FontAwesomeIcon icon={faCheck} />
          </DoraButton>
        </div>
      </form>

      {errors["percentage"] ? (
        <div className="income-handler-row__errors">
          <ValidationError id={fieldId} field="percentage" />
        </div>
      ) : null}
    </FormProvider>
  );
};

export default EditSurchargeRow;
